import { createApi } from "@reduxjs/toolkit/query/react";

import { ShapeFilterType } from "@/providers/store/api/shapes/enums/shape-filter-type.enum";
import type { IApiResponse } from "@/shared/interfaces/api-response.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";

import { upsertManyById, removeById, upsertAtTop } from "./utils/cache-helpers";

import type {
  IAdvancedFilterOptions,
  ICreateShapeMutation,
  ICustomColumnFilterOptions,
  IDeleteShapeMutation,
  IGetAdvancedFilterOptionsArg,
  IGetCustomColumnFilterOptionsArg,
  IGetShapesQuery,
  IGetShapesResponse,
  IGetProjectCountsInShapeArg,
  IGetSchoolCountsInShapeArg,
  IGetStudentCountsInShapeArg,
  IResolveShapeVisibilityMutation,
  IResolveShapeVisibilityResponse,
  IShape,
  IProjectCountsInShapeResult,
  ISchoolCountsInShapeResult,
  IStudentCountsInShapeResult,
  IUpdateShapeMutation,
} from "./interfaces/shapes.interface";

const listTagId = (districtId: string, type: ShapeFilterType) =>
  `LIST-${districtId}-${type}`;

const getSubscriptions = (
  districtId: string,
  type: ShapeFilterType,
  getState: () => any,
) =>
  shapesApi.util
    .selectInvalidatedBy(getState(), [
      { type: ApiTags.Shapes, id: listTagId(districtId, type) },
    ])
    .filter((x) => x.endpointName === "getShapes");

const createPatchList =
  (districtId: string, getState: () => any, dispatch: (action: any) => void) =>
    (type: ShapeFilterType, patchFn: (draft: IGetShapesResponse) => void) => {
      for (const s of getSubscriptions(districtId, type, getState)) {
        dispatch(
          shapesApi.util.updateQueryData(
            "getShapes",
          s.originalArgs as IGetShapesQuery,
          patchFn,
          ),
        );
      }
    };

const createPatchListWithUndo =
  (districtId: string, getState: () => any, dispatch: (action: any) => any) =>
    (
      type: ShapeFilterType,
      patchFn: (draft: IGetShapesResponse) => void,
    ): { undo: () => void }[] => {
      const patches: { undo: () => void }[] = [];
      for (const s of getSubscriptions(districtId, type, getState)) {
        const patch = dispatch(
          shapesApi.util.updateQueryData(
            "getShapes",
          s.originalArgs as IGetShapesQuery,
          patchFn,
          ),
        );
        patches.push(patch);
      }
      return patches;
    };

const shapesApi = createApi({
  reducerPath: "shapesApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.Shapes],
  endpoints: (builder) => ({
    getShapes: builder.query<IGetShapesResponse, IGetShapesQuery>({
      query: ({ districtId, type, page, limit }) => ({
        url: `/district/${districtId}/shapes`,
        method: "GET",
        params: { type, page, limit },
      }),
      transformResponse: (result: IApiResponse<IGetShapesResponse>) =>
        result.data,
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { districtId, type, limit } = queryArgs;
        return `${endpointName}-${districtId}-${type}-${limit}`;
      },
      merge: (currentCache, newData, { arg }) => {
        if (arg.page === 1) {
          currentCache.shapes = newData.shapes;
          currentCache.pagination = newData.pagination;
          return;
        }

        upsertManyById(currentCache.shapes, newData.shapes);
        currentCache.pagination = newData.pagination;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.districtId !== previousArg?.districtId ||
        currentArg?.type !== previousArg?.type ||
        currentArg?.limit !== previousArg?.limit ||
        currentArg?.page !== previousArg?.page,
      providesTags: (_result, _error, arg) => [
        { type: ApiTags.Shapes, id: listTagId(arg.districtId, arg.type) },
      ],
    }),
    createShape: builder.mutation<IShape, ICreateShapeMutation>({
      query: ({ districtId, body }) => ({
        url: `/district/${districtId}/shapes`,
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IShape>) => result.data,
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled;
          const patchList = createPatchList(arg.districtId, getState, dispatch);

          patchList(ShapeFilterType.MY, (draft) => {
            upsertAtTop(draft.shapes, created);
          });

          const isShared = created.districtShared;
          if (isShared) {
            patchList(ShapeFilterType.SHARED, (draft) => {
              upsertAtTop(draft.shapes, created);
            });
          }
        } catch (error) {
          console.error("Failed to create shape:", error);
        }
      },
    }),
    updateShape: builder.mutation<IShape, IUpdateShapeMutation>({
      query: ({ districtId, shapeId, body }) => ({
        url: `/district/${districtId}/shapes/${shapeId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (result: IApiResponse<IShape>) => result.data,
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: updated } = await queryFulfilled;
          const patchList = createPatchList(arg.districtId, getState, dispatch);

          patchList(ShapeFilterType.MY, (draft) => {
            upsertManyById(draft.shapes, [updated]);
          });

          const isShared =
            updated.districtShared ?? updated?.metadata?.districtShared;

          if (isShared) {
            patchList(ShapeFilterType.SHARED, (draft) => {
              upsertManyById(draft.shapes, [updated]);
            });
          } else {
            patchList(ShapeFilterType.SHARED, (draft) => {
              removeById(draft.shapes, String(updated.id));
            });
          }
        } catch (error) {
          console.error("Failed to update shape:", error);
        }
      },
    }),
    deleteShape: builder.mutation<void, IDeleteShapeMutation>({
      query: ({ districtId, shapeId }) => ({
        url: `/district/${districtId}/shapes/${shapeId}`,
        method: "DELETE",
      }),
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        const patchList = createPatchListWithUndo(
          arg.districtId,
          getState,
          dispatch,
        );
        const patchFn = (draft: IGetShapesResponse) =>
          removeById(draft.shapes, arg.shapeId);

        const patches = [
          ...patchList(ShapeFilterType.MY, patchFn),
          ...patchList(ShapeFilterType.SHARED, patchFn),
        ];

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
    }),
    resolveShapeVisibility: builder.mutation<
      IResolveShapeVisibilityResponse,
      IResolveShapeVisibilityMutation
    >({
      query: ({ districtId, body }) => ({
        url: `/district/${districtId}/shapes/resolve-visibility`,
        method: "POST",
        body,
      }),
      transformResponse: (
        result: IApiResponse<IResolveShapeVisibilityResponse>,
      ) => result.data,
    }),
    getStudentCountsInShape: builder.mutation<
      IStudentCountsInShapeResult,
      IGetStudentCountsInShapeArg
    >({
      query: ({
        districtId,
        districtWide,
        shapeIds,
        boundaryIds,
        nationalBoundariesIds,
        previewIds,
        customLayerBoundaryIds,
        mapLevelBoundaryIds,
        geometries,
        clusterGrid,
        schoolType,
        attendingSchoolIds,
        year,
        snapshotType,
        month,
        colorBy,
        gradeFrom,
        gradeTo,
        customAttributeFilters,
        advancedFilters,
        visibleSchoolTypes,
      }) => ({
        url: `/district/${districtId}/shapes/student-counts`,
        method: "POST",
        body: {
          districtWide,
          shapeIds,
          boundaryIds,
          nationalBoundariesIds,
          previewIds,
          customLayerBoundaryIds,
          mapLevelBoundaryIds,
          geometries,
          clusterGrid,
          schoolType,
          attendingSchoolIds,
          year,
          snapshotType,
          month,
          colorBy,
          gradeFrom,
          gradeTo,
          customAttributeFilters,
          advancedFilters,
          visibleSchoolTypes,
        },
      }),
      transformResponse: (result: IApiResponse<IStudentCountsInShapeResult>) =>
        result.data,
    }),
    getCustomColumnFilterOptions: builder.query<
      ICustomColumnFilterOptions,
      IGetCustomColumnFilterOptionsArg
    >({
      query: ({ districtId, year, snapshotType, month }) => ({
        url: `/district/${districtId}/shapes/custom-column-options`,
        method: "GET",
        params: { year, snapshotType, month },
      }),
      transformResponse: (result: IApiResponse<ICustomColumnFilterOptions>) =>
        result.data,
    }),
    getAdvancedFilterOptions: builder.query<
      IAdvancedFilterOptions,
      IGetAdvancedFilterOptionsArg
    >({
      query: ({ districtId, year, snapshotType, month }) => ({
        url: `/district/${districtId}/shapes/advanced-filter-options`,
        method: "GET",
        params: { year, snapshotType, month },
      }),
      transformResponse: (result: IApiResponse<IAdvancedFilterOptions>) =>
        result.data,
    }),
    getSchoolCountsInShape: builder.mutation<
      ISchoolCountsInShapeResult,
      IGetSchoolCountsInShapeArg
    >({
      query: ({
        districtId,
        districtWide,
        shapeIds,
        boundaryIds,
        nationalBoundariesIds,
        previewIds,
        customLayerBoundaryIds,
        mapLevelBoundaryIds,
        geometries,
        clusterGrid,
        visibleSchoolTypes,
      }) => ({
        url: `/district/${districtId}/shapes/school-counts`,
        method: "POST",
        body: {
          districtWide,
          shapeIds,
          boundaryIds,
          nationalBoundariesIds,
          previewIds,
          customLayerBoundaryIds,
          mapLevelBoundaryIds,
          geometries,
          clusterGrid,
          visibleSchoolTypes,
        },
      }),
      transformResponse: (result: IApiResponse<ISchoolCountsInShapeResult>) =>
        result.data,
    }),
    getProjectCountsInShape: builder.mutation<
      IProjectCountsInShapeResult,
      IGetProjectCountsInShapeArg
    >({
      query: ({
        districtId,
        shapeIds,
        boundaryIds,
        nationalBoundariesIds,
        previewIds,
        customLayerBoundaryIds,
        mapLevelBoundaryIds,
        geometries,
        clusterGrid,
        visibleReviewStatuses,
      }) => ({
        url: `/district/${districtId}/shapes/project-counts`,
        method: "POST",
        body: {
          shapeIds,
          boundaryIds,
          nationalBoundariesIds,
          previewIds,
          customLayerBoundaryIds,
          mapLevelBoundaryIds,
          geometries,
          clusterGrid,
          visibleReviewStatuses,
        },
      }),
      transformResponse: (result: IApiResponse<IProjectCountsInShapeResult>) =>
        result.data,
    }),
  }),
});

export const {
  useGetShapesQuery,
  useCreateShapeMutation,
  useUpdateShapeMutation,
  useDeleteShapeMutation,
  useResolveShapeVisibilityMutation,
  useGetStudentCountsInShapeMutation,
  useGetSchoolCountsInShapeMutation,
  useGetProjectCountsInShapeMutation,
  useGetCustomColumnFilterOptionsQuery,
  useGetAdvancedFilterOptionsQuery,
} = shapesApi;

export default shapesApi;
